import { put } from 'redux-saga/effects';
import { snackbarActions } from '../store/snackbarsSlice/snackbarsSlice';
import { formControlActions } from 'store/formControlSlice/formControlSlice';
import { transactionActions } from 'store/transactionsSlice/transactionsSlice';
import { transactionImageActions } from 'store/storeTransactionImage/storeTransactionImageSlice';
import { store } from 'store/store';
import { axiosAiq } from 'config';
import { kurentoConfig } from 'services/CameraStream/KurentoRtspSource/KurentoClient/KurentoConfig';
import { talkConfig } from 'services/Talk/TalkConfig';
import { cameraStreamManager } from 'services/CameraStream/CameraStreamManager';
import { TRANSACTION_PAGE_TABS } from 'config';
import {
    selectActiveTab,
    selectActiveTransactionId,
    selectHasReopenedTransaction,
    selectIsLanePinned,
    selectTranasctionById,
    selectTransactionsSplitViewMode,
} from 'store/transactionsSlice/transactionsSelectors';
import { talkManager } from 'services/Talk/TalkManager';
import { hasOpenSubmitBlockDialog } from './submitBlocking';
import { CAMERA, PROPERTY_NOT_FOUND } from './constants';
import { kurentoClientManager } from 'services/CameraStream/KurentoRtspSource/KurentoClient/KurentoClientManager';

const {
    DESKS_TAB: { ID: desksTabId },
    REOPENED_TRANSACTION_TAB: { ID: reopenedTransactionTabId },
} = TRANSACTION_PAGE_TABS;

export let keysPressed = {};

//////Event handlers on Transactions page/////////////////////////////////////////////////////////////////////////
export const fullScreenChangeHandler = () => !document.fullscreenElement && store.dispatch(transactionActions.setRequestFullScreenForTabId(0));

// Shared by the Ctrl and Alt quick-key branches below, AND by useSplitViewKeyboard.js's
// Ctrl+Shift+Enter/Delete branch (Split View) - exported for that reason. On a pinned lane
// (startNew=true) it submits/discards AND starts the next transaction; otherwise it performs a
// plain submit/discard. `form` defaults to `true` (i.e. no gate) because Split View has never
// gated quick-submit on a document.getElementById(transactionId) lookup the way this file's
// keyDownHandler does; omitting `form` keeps matching Split View's own prior unconditional
// dispatch. Uses store.dispatch directly (not a passed-in dispatch) - safe because both call sites
// share the single global store, so this is equivalent to dispatching via useDispatch().
export function dispatchTransactionQuickKey({
    e,
    activeTransaction,
    key,
    startNew,
    form = true,
    pulseShortcutUsed = false,
    openedViaPulseShortcut = false,
}) {
    if (!activeTransaction) return;
    if (e.target.tagName === 'INPUT') {
        e.target.inputBlurOnQuickSubmit = true;
        e.target.blur();
    }
    const { id: transactionId, locationId, tabId, gateId } = activeTransaction;

    if (form && key === 'ENTER') {
        store.dispatch(
            (startNew ? formControlActions.submitFormAndStartNewStart : formControlActions.submitFormStart)({
                transactionId,
                locationId,
                tabId,
                gateId,
                autoPopulateEmptyFormFields: true,
                pulseShortcutUsed,
            })
        );
        store.dispatch(transactionImageActions.confirmTransaction({ transactionId }));
    }

    if (key === 'DELETE') {
        store.dispatch(formControlActions.setDiscardReasonDialogOpen({ transactionId, startNew, openedViaPulseShortcut }));
        store.dispatch(transactionImageActions.discardTransaction({ transactionId }));
    }
}

const ALT_CLAIMED_KEYS = new Set(['ENTER', 'DELETE', 'Z']);
const CTRL_CLAIMED_KEYS = new Set(['ENTER', 'DELETE', 'O', 'M', 'D', 'Q', 'E']);
const isDigitKey = (key) => key.length === 1 && key >= '0' && key <= '9';

// Split per modifier: a single set would also swallow Ctrl+Z, which the dialogs need for undo.
const isKeyClaimedByKeyDownHandler = (key, e) => {
    if (e.altKey && (ALT_CLAIMED_KEYS.has(key) || isDigitKey(key))) return true;
    return e.ctrlKey && (CTRL_CLAIMED_KEYS.has(key) || isDigitKey(key));
};

export const keyDownHandler = function (e) {
    keysPressed[e.key] = true;
    const state = store.getState();
    const key = e.key.toUpperCase();

    if (hasOpenSubmitBlockDialog(state.forms)) {
        if (isKeyClaimedByKeyDownHandler(key, e)) e.preventDefault();
        return;
    }

    const activeTab = selectActiveTab(state);
    const transactionSlice = state.transactions;
    const activeTransactionId = selectActiveTransactionId(state);
    const { transactions: allTransactions } = transactionSlice;

    if (Object.hasOwn(keysPressed, 'Alt') && key === 'Z') {
        e.preventDefault();
        store.dispatch(transactionActions.setRequestFullScreenForTabId(activeTab));
    }

    // Alt + Enter / Alt + Del always submit/discard AND start a new transaction (mirrors the "…and start new"
    // buttons/menu items, which are offered regardless of pin state - it degrades gracefully on an unpinned
    // lane). Ctrl + Enter / Ctrl + Del do the same, but only on a pinned lane (see below).
    if (Object.hasOwn(keysPressed, 'Alt') && (key === 'ENTER' || key === 'DELETE') && activeTransactionId) {
        e.preventDefault();
        const activeTransaction = selectTranasctionById(activeTransactionId)(state);

        dispatchTransactionQuickKey({ e, activeTransaction, key, startNew: true, form: document.getElementById(activeTransactionId) });
        return;
    }

    if (!Object.hasOwn(keysPressed, 'Control')) return;

    if (isFinite(key) || key === 'O') {
        e.preventDefault();
        const isSplitViewMode = selectTransactionsSplitViewMode(state);
        if (isSplitViewMode) return;

        const hasReopenedTransaction = selectHasReopenedTransaction(state);
        let tabId;
        if (isFinite(key)) {
            const index = hasReopenedTransaction ? +key : +key - 1;
            tabId = allTransactions[index]?.tabId;
            if (+key === 0) tabId = desksTabId;
        }
        key === 'O' && hasReopenedTransaction && (tabId = reopenedTransactionTabId);

        if (tabId) store.dispatch(transactionActions.setActiveTab(tabId));
    }

    if (!activeTransactionId) return;

    const form = document.getElementById(activeTransactionId);

    // Ctrl+Shift+Enter / Ctrl+Shift+Delete — Quick Submit/Discard, plus an automatic Pulse when the
    // lane's automaticPulse flag ('NONE' | 'SUBMIT' | 'ALL') covers this action. The submit/discard
    // ALWAYS happens on this shortcut, exactly like plain Ctrl+Enter/Delete below. Whether the Pulse
    // actually fires (or a "no Pulse relay configured" warning is shown instead) is decided in
    // submitForm()/discardTransaction() (formControlSagas/index.js) once the submit/discard has
    // actually succeeded - not here, before that is known.
    if (Object.hasOwn(keysPressed, 'Shift') && (key === 'ENTER' || key === 'DELETE')) {
        e.preventDefault();
        const activeTransaction = selectTranasctionById(activeTransactionId)(state);
        if (!activeTransaction) return;
        const isLanePinned = selectIsLanePinned(activeTransaction.gateId)(state);
        dispatchTransactionQuickKey({
            e,
            activeTransaction,
            key,
            startNew: isLanePinned,
            form,
            pulseShortcutUsed: true,
            openedViaPulseShortcut: true,
        });
        return;
    }

    if (key === 'ENTER' || key === 'DELETE') {
        e.preventDefault();
        if (e.target.tagName === 'INPUT') {
            e.target.inputBlurOnQuickSubmit = true;
            e.target.blur();
        }
        const activeTransaction = selectTranasctionById(activeTransactionId)(state);
        // On a pinned lane the quick-keys submit/discard AND start the next transaction (same as the buttons);
        // on an unpinned lane they behave as before.
        const isLanePinned = selectIsLanePinned(activeTransaction?.gateId)(state);

        dispatchTransactionQuickKey({ e, activeTransaction, key, startNew: isLanePinned, form });
    }

    if (key === 'M') {
        e.preventDefault();
        const tabId = transactionSlice.activeTab;
        const tabsAudioObj = transactionSlice.tabsAudio;
        const muted = tabsAudioObj?.[tabId]?.isTabMuted;
        const soundOnStreamId = tabsAudioObj?.[tabId]?.soundOnStreamId;
        store.dispatch(transactionActions.setTabAudioMuted({ tabId, muted: !muted }));
        muted && soundOnStreamId && cameraStreamManager.enableAudio(soundOnStreamId);
    }

    if (key === 'D') {
        e.preventDefault();
        talkManager.toggleOutgoingAudioActive();
    }

    if (key !== 'Q' && key !== 'E') return;
    e.preventDefault();
    let element;
    switch (key) {
        case 'Q':
            element = form.querySelector(`input:not([disabled]):not([type='hidden'])`);
            break;
        case 'E':
            element = form.querySelector(`input[value='']:not([disabled]):not([type='hidden']`);
            break;
        default:
    }

    element.select();
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
};

export const keyUpHandler = (e) => {
    delete keysPressed[e.key];
};

export const clearKeyPressedObject = () => {
    keysPressed = {};
};

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

// Handling writing and reading from objects ////////////////////////////////////////////////////////////////////////
function createNameArray(namePath) {
    const nameArr = namePath.split(/[[\].]{1,2}/).reduce((acc, item) => {
        if (item === '') return acc;
        acc.push(item);
        return acc;
    }, []);
    const maxIndex = nameArr.length - 1;
    return [nameArr, maxIndex];
}

export const readFromObj = function (stateObj, namePath, del = false) {
    const [nameArr, maxIndex] = createNameArray(namePath);
    let property = stateObj;
    if (property)
        nameArr.forEach((item, i) => {
            if (property == undefined || property === PROPERTY_NOT_FOUND) return;
            if (item in property) {
                if (i === maxIndex && del) {
                    delete property[item];
                }
                property = property[item];
            } else {
                property = PROPERTY_NOT_FOUND;
            }
        });

    return property;
};

export const writeInObj = function (stateObj, namePath, value, options = {}) {
    if (!stateObj || !namePath) return;
    const { action, index } = options;
    const [nameArr, maxIndex] = createNameArray(namePath);

    let property = stateObj;

    nameArr.forEach((item, i) => {
        if (i === maxIndex) {
            if (property === null) return;
            if (
                // (!value && value !== '' && !property[item]) ||
                property?.[item] === value ||
                !(item in property)
            )
                return;

            switch (action) {
                case undefined:
                    property[item] = value;

                    break;

                case 'push':
                    property[item].push(value);
                    break;

                case 'remove':
                    property[item].splice(index, 1);
                    break;
                default:
            }
            return;
        }

        if (item in property) {
            property = property[item];
        } else {
            // property[item] = namePath.includes(`[${arr[i + 1]}]`) ? [] : {};
            // property = property[item];
            return;
        }
    });
};

export function createFormConfigObject(obj, path = '') {
    let output = {};
    for (let key in obj) {
        let newPath = path === '' ? key : `${path}.${key}`;
        switch (true) {
            case typeof obj[key] !== 'object':
            case Array.isArray(obj[key]):
                output[newPath] = obj[key];
                newPath = path;
                break;

            default:
                output = {
                    ...output,
                    ...createFormConfigObject(obj[key], newPath),
                };
        }
    }
    return output;
}
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export function* notificationHandler({ err, action, title, variant = 'error' }) {
    if (action)
        yield put(
            action({
                title: title,
                message: err?.message || '',
                code: err?.code || '',
            })
        );

    if (err?.response?.status === 401) return;
    const errorDetails = err ? `|| CODE: ${err.code}  ||  MESSAGE: ${err.message}` : '';
    const message = `${title.toUpperCase()} ${errorDetails}`;
    console.log(`[User notification] ${message}`);

    yield put(
        snackbarActions.enqueueSnackbar({
            message,
            options: {
                variant: variant,
            },
        })
    );
}

export function notificationHandlerSync({ err, action, title, variant = 'error' }) {
    if (action)
        store.dispatch(
            action({
                title: title,
                message: err?.message || '',
                code: err?.code || '',
            })
        );

    if (err?.response?.status === 401) return;
    const errorDetails = err ? `|| CODE: ${err.code}  ||  MESSAGE: ${err.message}` : '';
    const message = `${title.toUpperCase()} ${errorDetails}`;
    console.log(`[User notification] ${message}`);

    store.dispatch(
        snackbarActions.enqueueSnackbar({
            message,
            options: {
                variant: variant,
            },
        })
    );
}

export async function getKurentoConfig() {
    try {
        const response = await axiosAiq('/admin/kurento/configuration/multiple');
        const configurations = response.data || [];
        kurentoConfig.configuration = configurations;
        talkConfig.configuration = configurations[0]?.talkService;
        return true;
    } catch (err) {
        err.response?.status !== 401 &&
            store.dispatch(
                snackbarActions.enqueueSnackbar({
                    message: `ERROR FETCHING KURENTO CONFIGURATION`,
                    options: {
                        variant: 'error',
                    },
                })
            );
    }
}

//////  LOG CUSTOM TEXT WITH CAMERA AND TRANSACTION INFO TO GRAFANA

export function logInfo(initialString, data) {
    const { camConfig, transactionInfo, camVideoId } = data;

    let string = `${initialString} ${logCameraInfo({
        ...camConfig,
        camVideoId,
    })}, ${transactionInfo?.uuid ? logTransactionInfo({ ...transactionInfo, filedName: data.fieldName }) : `environment="${transactionInfo.id} page"`} 
        `;

    console.log(string); //this will be sent to grafana
}

export const logTransactionInfo = (ti) =>
    `deskId=${ti.deskId}, transactionUuid="${ti.uuid}", transactionId=${ti.id} , locationId=${ti.locationId} , locationName="${
        ti.locationName
    }", gateId=${ti.gateId}, gateName="${ti.gateName}"${ti.filedName ? `, filedName="${ti.filedName}"` : ''}"`;

export const logCameraInfo = (cc) =>
    !cc.camId
        ? ''
        : `, cameraName="${cc?.name}", cameraType="${cc?.type}", cameraTab="${cc?.camVideoId}", url="${cc?.videoData?.url}"${cc?.talkUrl ? `, talkUrl="${cc?.talkUrl}"` : ''}", cameraId=${cc?.camId}`;

////// TRANSFORM CAMERA CONFIGURATION GOT FROM BE

/// this is pure function (it may be called inside reducer)
export function transformCameraConfiguration(camConfig, id) {
    const isActive = !camConfig.status || camConfig.status === CAMERA.STATUS_ACTIVE;
    const isHikvisionTWA = camConfig.talk_url && camConfig.audio_type === CAMERA.AUDIO_TYPE.HIKVISION_2WA && isActive;
    const is3CXTWA = camConfig.extension_number && camConfig.audio_type === CAMERA.AUDIO_TYPE.EXTENSION_3CX && isActive;
    return {
        ...(id && { id }),
        camId: camConfig.camera_id ? camConfig.camera_id : camConfig.id,
        gateCameraId: camConfig.id,
        talkUrl: isHikvisionTWA ? camConfig.talk_url : '',
        extensionNumber3CX: is3CXTWA ? camConfig.extension_number : '',
        kurentoId: camConfig.kurento_id,
        invalidated: !kurentoClientManager.isKurentoActive(camConfig.kurento_id),
        previewCamera: !!camConfig.preview_camera,
        videoWallCamera: !!camConfig.video_wall_camera,
        auxiliaryCamera: !!camConfig.auxiliary_camera,
        gateId: camConfig?.gate_id,
        ...(camConfig.name && { name: camConfig.name }),
        ...(camConfig.camera_type && { type: camConfig.camera_type }),
        ...(camConfig.gate_name && { gateName: camConfig.gate_name }),
        ...(camConfig.direction && { direction: camConfig.direction }),
        ...(camConfig.motion_detection_camera !== undefined && { motionDetectionCamera: camConfig.motion_detection_camera }),
        ...(camConfig.generated_name && { generatedName: camConfig.generated_name }),
        ...(camConfig.location_name && { locationName: camConfig.location_name }),
        ...(camConfig.level && { level: camConfig.level }),
        ...(camConfig.relays && { relays: camConfig.relays }),
        ...(camConfig.mtx_direct && { mtxDirect: camConfig.mtx_direct }),
        ...(camConfig.issues && { issues: camConfig.issues }),
        ...(camConfig.in_saturation && { inSaturation: camConfig.in_saturation }),
        ...(camConfig.status && { status: camConfig.status }),
        ...(camConfig.audio_type && { audioType: camConfig.audio_type }),
        ...(camConfig.has_intercom && { hasIntercom: camConfig.has_intercom }),
        ...(camConfig.active_webhook && { activeWebhook: camConfig.active_webhook }),
        ...(camConfig.aidetection_on && { isAIDetectionOn: camConfig.aidetection_on }),
        recordingUrl: camConfig.recording_url || '',
        recordingPath: camConfig.recording_path || '',
        videoData: {
            url: camConfig.url,
            playerEndpointId: camConfig['player_id'],
            ...(camConfig.stream_type && { streamType: camConfig.stream_type }),
        },
    };
}

export const flattenCamsConfig = (camsConfig) => [...(camsConfig?.level1 ?? []), ...(camsConfig?.level2 ?? [])];

export const resolveVideoWallCameraFromList = (configs) => configs?.find((camConfig) => camConfig.videoWallCamera) ?? configs?.[0];

export const resolveVideoWallGateCameraId = (camsConfig) => {
    const configs = flattenCamsConfig(camsConfig);
    if (!configs.length) return undefined;
    return resolveVideoWallCameraFromList(configs)?.gateCameraId;
};

export function getCookie(cname) {
    let name = cname + '=';
    let decodedCookie = decodeURIComponent(document.cookie);
    let ca = decodedCookie.split(';');
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') {
            c = c.substring(1);
        }
        if (c.indexOf(name) === 0) {
            return c.substring(name.length, c.length);
        }
    }
    return '';
}

export const hasDataInSection = (section, formState) => {
    const values = formState?.values;
    const automation = formState?.automation;
    // Check if section exists in values and contains a property with a value
    if (values && Object.keys(values).some((key) => key.startsWith(section) && values[key] !== null && values[key] !== '')) {
        return true;
    }
    // Check if section exists in automation and contains sourceValue
    return automation && automation[section] && automation[section].sourceValue;
};

export const formatErrorCode = (errorCode) => {
    return errorCode.replace(/_/g, ' ').toLowerCase();
};

export const errorCodeToMessageMap = {
    NO_SUITABLE_DESK_FOUND: 'No other agents are available. The transaction will remain open on this desk.',
    // Sticky lanes
    GATE_ALREADY_STICKY: 'This lane is already pinned to another desk.',
    GATE_STICKY_TO_ANOTHER_DESK: 'This lane is pinned to another desk.',
    STICKY_GATE_NOT_FOUND: 'This lane is no longer pinned.',
    MAX_STICKY_GATES_REACHED: 'The maximum number of pinned lanes has been reached.',
    DESK_INACTIVE: 'Your desk is not active.',
    DESK_ONE_ACCOUNT_ALLOWED: 'Only one account can be active on a desk.',
};

export const getFormattedErrorMessage = (errorMsg) => {
    if (!errorMsg) return 'An unknown error occurred';

    const errorCodeMatch = errorMsg.match(/\[([^\]]{1,100})\]/);
    if (!errorCodeMatch) return 'An unknown error occurred';

    const errorCode = errorCodeMatch[1];
    return errorCodeToMessageMap[errorCode] || formatErrorCode(errorCode);
};

export const parseErrorMessageFromResponse = (error, defaultMessage = 'An unknown error occurred') => {
    const errorMsgMatch = error.match(/\[([^\]]+)\] \[([^\]]+)\]/);
    return errorMsgMatch ? errorMsgMatch[2] : defaultMessage;
};

/**
 * Recursively normalizes special/smart Unicode quote characters in strings
 * to their standard ASCII equivalents.
 *
 * Handles the following replacements:
 *   \u2018 (') LEFT SINGLE QUOTATION MARK  → ' (U+0027)
 *   \u2019 (') RIGHT SINGLE QUOTATION MARK → ' (U+0027)
 *   \u201C (") LEFT DOUBLE QUOTATION MARK  → " (U+0022)
 *   \u201D (") RIGHT DOUBLE QUOTATION MARK → " (U+0022)
 *   \u2032 (′) PRIME                       → ' (U+0027)
 *   \u2033 (″) DOUBLE PRIME                → " (U+0022)
 *
 * This is needed because the database stores smart/curly quotes (e.g. in
 * company names like "Gibson\u2019s") which AIQ cannot render, causing the
 * field to appear blank or truncated.
 */
export function normalizeSpecialCharacters(value) {
    if (typeof value === 'string') {
        return value.replace(/[\u2018\u2019\u2032]/g, "'").replace(/[\u201C\u201D\u2033]/g, '"');
    }
    if (Array.isArray(value)) {
        return value.map(normalizeSpecialCharacters);
    }
    if (value !== null && typeof value === 'object') {
        return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, normalizeSpecialCharacters(v)]));
    }
    return value;
}

export const convertText = (originalText) => {
    let text = originalText.replace('[PARTIAL] ', '').trim();

    const parts = text.split(', [');

    if (parts.length >= 2) {
        let firstPart = parts[0];
        if (firstPart.startsWith('[')) {
            firstPart = firstPart.slice(1);
        }
        firstPart = firstPart.replace('transaction..', 'transaction');

        let numbersPart = parts[1];
        numbersPart = numbersPart.replace(']', '');

        return `${firstPart}, ${numbersPart} not discarded`;
    }

    return originalText;
};
